import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Table, Form, InputGroup } from "react-bootstrap";
import Swal from "sweetalert2";
import { useTerritorio } from "../../context/TerritorioContext";
import { useBacAuth } from "../../context/BacAuthContext";
import BacSarampionModal from "../modal/BacSarampionModal";
import HistorialViviendaModal from "../modal/HistorialViviendaModal";
import VerVisitaModal from "../modal/VerVisitaModal";
import {
  useOnlineStatus,
  cachearBacVisitas,
  leerBacVisitasCache,
} from "../../hooks/useSync";
import { db } from "../../db/localDB";
import { getVisitasBac, exportarVisitasBac, deleteVisitaBac } from "../api/bacSarampionAPI.js";
import API from "../../axios.jsx";
import SelectorJornada, { coincideJornada } from "../jornadas/SelectorJornada";
import { exportarFiltradoBac } from "../api/bacSarampionAPI.js";
import { paramsJornada, nombreExcel, descargarBlob } from "../jornadas/exportarExcel";

// Íconos SVG propios — se ven igual en cualquier navegador/SO, a
// diferencia de los emoji (que renderizan distinto según la fuente del
// sistema, algunos en color y otros en blanco/negro).
const IconoOjo = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconoCamara = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const IconoRevisitar = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);
const IconoMapa = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IconoEliminar = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const IconoEditar = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);


const BTN_ACCION = {
  background: "none", border: "none", cursor: "pointer",
  width: 30, height: 30, borderRadius: 7,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};

const ESTADO_DOT = {
  Efectiva:     "#2d6a4f",
  "No efectiva": "#a15c9e",
  Cerrada:      "#b7791f",
  Renuente:     "#b3261e",
  "No habitada": "#8b95a1",
};

const TH = { padding: "11px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 12 };
const TD = { padding: "10px 12px", verticalAlign: "middle", fontSize: 13 };
const REGISTROS_PAG = 10;

export default function TablaBacSarampion({ soloLectura = false, onVerEnMapa, puedeExportar = true, esAdmin = false,
  jornadaActiva = null, jornadas = [], filtroJornada = "", onFiltroJornada }) {
  const { data: ctx } = useTerritorio();
  const { usuario } = useBacAuth();
  // El admin siempre puede editar — quien no es admin, solo si el
  // registro quedó a su propio nombre (campo "entrevistador").
  // Dentro de una jornada que NO está abierta (cerrada o programada) todo
  // es de consulta: no se registran visitas ni revisitas, y solo el admin
  // corrige registros.
  const sinRegistro = soloLectura || (!!filtroJornada && !jornadaActiva);
  const puedeEditar = (r) => esAdmin || (!sinRegistro && r.entrevistador && r.entrevistador === usuario?.nombre);
  const [verPendientesPrevias, setVerPendientesPrevias] = useState(false);
  const online         = useOnlineStatus();

  // Reconoce "Renuente" en las 2 formas que puede venir guardado: la
  // vieja (estado_vivienda = "Renuente" directo, registros de antes del
  // cambio a Efectiva/No efectiva) y la nueva (estado_vivienda = "No
  // efectiva" con motivo_no_efectiva = "Renuente").
  const esRenuente = (r) => r.estado_vivienda === "Renuente" || (r.estado_vivienda === "No efectiva" && r.motivo_no_efectiva === "Renuente");
  // Revisita solo tiene sentido si de verdad hay chance de encontrar a
  // alguien la próxima vez — Cerrada, o No efectiva con motivo Cerrada
  // o Menor en casa (Renuente/No habitada/Abandona no aplican).
  const requiereRevisita = (r) => r.estado_vivienda === "Cerrada"
    || (r.estado_vivienda === "No efectiva" && (r.motivo_no_efectiva === "Cerrada" || r.motivo_no_efectiva === "Menor en casa"));

  const [todas,        setTodas]        = useState([]); // TODAS las visitas (cada intento)
  const [showModal,    setShowModal]    = useState(false);
  const [modoRevisita, setModoRevisita] = useState(false);
  const [viviendaBase, setViviendaBase] = useState(null); // fila de referencia (nueva o para revisitar)
  const [visitaEditar, setVisitaEditar] = useState(null); // registro completo a editar (admin)

  const [showHistorial, setShowHistorial] = useState(false);
  const [codigoHistorial, setCodigoHistorial] = useState(null);

  const [showVer, setShowVer] = useState(false);
  const [visitaVer, setVisitaVer] = useState(null);

  const [busqueda,     setBusqueda]     = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroEntrevistador, setFiltroEntrevistador] = useState("");
  const [fechaInicio,  setFechaInicio]  = useState("");
  const [fechaFin,     setFechaFin]     = useState("");
  const [cargando,     setCargando]     = useState(false);
  const [exportando,   setExportando]   = useState(false);
  const [pagina,       setPagina]       = useState(1);
  const [tarjetaActiva, setTarjetaActiva] = useState(null);

  // ── Cargar (agrupa por codigo_vivienda, se queda con la más reciente) ──
  const cargar = useCallback(async (silencioso = false) => {
    if (!ctx?.territorio) { setTodas([]); return; }
    if (!silencioso) setCargando(true);

    let datosBase = [];
    if (online) {
      try {
        const res = await getVisitasBac({ territorio: ctx.territorio, comunidad: ctx.comunidad });
        datosBase = res.data || [];
        await cachearBacVisitas(ctx.territorio, ctx.comunidad, datosBase);
      } catch (e) {
        console.warn("⚠️ No se pudo cargar del servidor, usando cache local:", e.message);
        datosBase = await leerBacVisitasCache(ctx.territorio, ctx.comunidad);
      }
    } else {
      datosBase = await leerBacVisitasCache(ctx.territorio, ctx.comunidad);
    }

    // Visitas creadas offline sin sincronizar — se muestran con badge
    // "⏳ pendiente", igual criterio que Tamizaje.
    let pendientes = [];
    try {
      pendientes = await db.bac_visitas_pendientes
        .filter(r => r.sincronizado === 0 && !r._editId && !r._deleteId
          && r.territorio === ctx.territorio && (r.comunidad || "") === (ctx.comunidad || ""))
        .toArray();
      pendientes = pendientes.map(r => ({
        ...r,
        _localId: r.id, // el id real en Dexie — se necesita para poder editar este borrador
        id: `local_${r.id}`,
        codigo_vivienda: r.codigo_vivienda || r.codigo_vivienda_previo,
        _pendiente: true,
      }));
    } catch { /* noop */ }

    setTodas([...pendientes, ...datosBase]);
    if (!silencioso) setCargando(false);
  }, [ctx?.territorio, ctx?.comunidad, online]);

  // Filtro de jornada ANTES de agrupar: así una vivienda revisitada en
  // otra jornada aparece en cada jornada con su intento de esa jornada.
  const lista = useMemo(() => {
    // Agrupar por codigo_vivienda, quedarse solo con la más reciente
    // (mayor numero_visita; los pendientes offline sin numero_visita
    // resuelto se tratan como "la más reciente" de su grupo igual, ya
    // que representan el intento más nuevo que el usuario acaba de hacer).
    const porVivienda = new Map();
    for (const v of todas.filter(t => coincideJornada(t.jornada_codigo, filtroJornada))) {
      const clave = v.codigo_vivienda;
      const actual = porVivienda.get(clave);
      if (!actual || v._pendiente || (v.numero_visita || 0) >= (actual.numero_visita || 0)) {
        porVivienda.set(clave, v);
      }
    }

    return [...porVivienda.values()];
  }, [todas, filtroJornada]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPagina(1); }, [busqueda, filtroEstado, filtroEntrevistador, tarjetaActiva, fechaInicio, fechaFin, filtroJornada]);
  useEffect(() => {
    const handler = () => cargar();
    window.addEventListener("sync-completado", handler);
    return () => window.removeEventListener("sync-completado", handler);
  }, [cargar]);

  // ── Polling silencioso ──────────────────────────────────────────────
  // Cada 45s vuelve a traer la lista EN SEGUNDO PLANO (sin "Cargando...",
  // sin tocar la página en la que estás parado) — para que la tabla se
  // mantenga al día si otras personas están registrando visitas desde
  // otros dispositivos al mismo tiempo.
  useEffect(() => {
    if (!online) return; // sin conexión no hay nada nuevo que traer
    const intervalo = setInterval(() => cargar(true), 45000);
    return () => clearInterval(intervalo);
  }, [cargar, online]);

  const sinJornada = () => {
    Swal.fire({
      icon: "info", title: "Esta jornada no está abierta",
      html: `Solo se registran visitas en una jornada <b>abierta</b>.<br>Usa <b>📅 Cambiar jornada</b> para ingresar a la jornada abierta de ${ctx?.comunidad || "la comunidad"} (o abrir una nueva).`,
      confirmButtonColor: "#16342a",
    });
  };

  // ── Nueva visita (vivienda nueva) ───────────────────────────────────
  const abrirNuevaVisita = () => {
    if (!jornadaActiva) return sinJornada();
    setModoRevisita(false);
    setViviendaBase(null);
    setShowModal(true);
  };

  // ── Revisitar (misma vivienda, nueva fila con el mismo codigo) ─────
  const abrirRevisita = (vivienda) => {
    if (!jornadaActiva) return sinJornada();
    setModoRevisita(true);
    setViviendaBase(vivienda);
    setShowModal(true);
  };

  // La fila ya trae todo (incluido pai_vacunacion anidado) — sin fetch extra.
  const abrirEditar = (vivienda) => {
    setModoRevisita(false);
    setViviendaBase(null);
    setVisitaEditar(vivienda);
    setShowModal(true);
  };


  const verHistorial = (codigoVivienda) => {
    setCodigoHistorial(codigoVivienda);
    setShowHistorial(true);
  };

  // ── Ver detalle ──────────────────────────────────────────────────────
  const verDetalle = (vivienda) => {
    setVisitaVer(vivienda);
    setShowVer(true);
  };

  // ── Eliminar (solo admin) ────────────────────────────────────────────
  const eliminar = async (r) => {
    const confirmado = await Swal.fire({
      icon: "warning", title: `¿Eliminar este intento de ${r.codigo_vivienda}?`,
      text: "Esta acción no se puede deshacer.",
      showCancelButton: true, confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", confirmButtonColor: "#b3261e",
    });
    if (!confirmado.isConfirmed) return;
    try {
      await deleteVisitaBac(r.id);
      await cargar();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo eliminar", "error");
    }
  };

  // ── Exportar — según el alcance elegido ──────────────────────────────
  // Excel SIEMPRE de la jornada en la que se está. Si la tabla tiene algún
  // filtro aplicado (búsqueda, estado, fechas, tarjeta…), sale solo lo filtrado.
  const exportar = async () => {
    setExportando(true);
    try {
      const params = paramsJornada(ctx, filtroJornada);
      const conFiltro = filtrados.length !== lista.length;
      const res = conFiltro
        ? await exportarFiltradoBac("visitas", { params, codigos: filtrados.filter(r => !r._pendiente).map(r => r.codigo_vivienda) })
        : await exportarVisitasBac(params);
      descargarBlob(res.data, nombreExcel("BAC_Visitas", ctx, jornadas, filtroJornada, conFiltro));
    } catch {
      Swal.fire("Error", "No se pudo exportar", "error");
    } finally {
      setExportando(false);
    }
  };


  // ── Dashboard filtrable ──────────────────────────────────────────────
  const TARJETAS_DASHBOARD = [
    { id: "total",     label: "Total",              bg: "rgba(255,255,255,0.18)", predicate: () => true },
    { id: "efectiva",  label: "Efectivas",        bg: "rgba(45,106,79,0.35)",   predicate: r => r.estado_vivienda === "Efectiva" || esRenuente(r) },
    { id: "pendiente", label: "Por revisitar",    bg: "rgba(183,121,31,0.35)",  predicate: r => requiereRevisita(r) },
    { id: "eisp",      label: "EISP encontrado",  bg: "rgba(179,38,30,0.35)",   predicate: r => r.eisp_encontrado === "Si" },
    { id: "sin_sync",  label: "Sin sincronizar",  bg: "rgba(251,191,36,0.3)",   predicate: r => r._pendiente },
  ].map(t => ({ ...t, valor: lista.filter(t.predicate).length }));

  const tarjetaSeleccionada = TARJETAS_DASHBOARD.find(t => t.id === tarjetaActiva);

  const entrevistadoresPresentes = [...new Set(lista.map(r => r.entrevistador).filter(Boolean))].sort();

  const filtrados = lista.filter(r => {
    const q = busqueda.toLowerCase();
    const matchQ = !busqueda
      || r.codigo_vivienda?.toLowerCase().includes(q)
      || r.direccion?.toLowerCase().includes(q)
      || r.nombre_entrevistado?.toLowerCase().includes(q);
    const matchEstado = !filtroEstado || r.estado_vivienda === filtroEstado;
    const matchEntrevistador = !filtroEntrevistador || r.entrevistador === filtroEntrevistador;
    const fechaRegistro = r.createdAt?.split("T")[0];
    const matchInicio = !fechaInicio || (fechaRegistro && fechaRegistro >= fechaInicio);
    const matchFin    = !fechaFin    || (fechaRegistro && fechaRegistro <= fechaFin);
    const matchTarjeta = !tarjetaSeleccionada || tarjetaSeleccionada.predicate(r);
    return matchQ && matchEstado && matchEntrevistador && matchInicio && matchFin && matchTarjeta;
  });

  // Último intento de cada vivienda en TODA la comunidad (sin filtro de
  // jornada): las que siguen pendientes de revisita y cuyo último intento
  // quedó en otra jornada.
  const pendientesPrevias = (() => {
    if (!filtroJornada) return [];
    const ultimo = new Map();
    for (const v of todas) {
      const a = ultimo.get(v.codigo_vivienda);
      if (!a || v._pendiente || (v.numero_visita || 0) >= (a.numero_visita || 0)) ultimo.set(v.codigo_vivienda, v);
    }
    return [...ultimo.values()]
      .filter(v => requiereRevisita(v) && !coincideJornada(v.jornada_codigo, filtroJornada))
      .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  })();

  const totalPags = Math.ceil(filtrados.length / REGISTROS_PAG);
  const paginados = filtrados.slice((pagina - 1) * REGISTROS_PAG, pagina * REGISTROS_PAG);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header + dashboard filtrable */}
      <div className="bac-hero" style={{
        background: "linear-gradient(135deg, #16342a 0%, #1f4a3a 60%, #2d6a4f 100%)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 20, color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="bac-hero-eyebrow" style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
              🦟 Búsqueda Activa Comunitaria — Sarampión · 2026
            </div>
            <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.15rem" }}>Registro de visitas</h5>
            {ctx?.territorio && (
              <div className="bac-hero-lugar" style={{ marginTop: 5, fontSize: 13, opacity: 0.85 }}>
                📍 {ctx.territorio} {ctx.comunidad ? `· 🏘 ${ctx.comunidad}` : ""}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TARJETAS_DASHBOARD.map(({ id, label, valor, bg }) => {
              const seleccionada = tarjetaActiva === id;
              return (
                <button key={id} className={`bac-stat${seleccionada ? " is-on" : ""}`} data-tono={id} onClick={() => setTarjetaActiva(seleccionada ? null : id)}
                  title={seleccionada ? "Clic para quitar el filtro" : `Filtrar por: ${label}`}
                  style={{
                    background: bg, borderRadius: 10, padding: "7px 14px", textAlign: "center", minWidth: 70,
                    cursor: "pointer", border: seleccionada ? "2px solid #fff" : "2px solid transparent",
                    boxShadow: seleccionada ? "0 0 0 2px rgba(255,255,255,.5)" : "none",
                  }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff" }}>{valor}</div>
                  <div style={{ fontSize: 10, opacity: 0.9, color: "#fff" }}>{label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {sinRegistro && filtroJornada && (
        <div className="mb-3 px-3 py-2 rounded" style={{ background: "#eef0f2", fontSize: 13, textAlign: "left" }}>
          🔒 Esta jornada no está abierta: es solo de consulta. Para registrar visitas usa <b>📅 Cambiar jornada</b> e ingresa a la jornada abierta (o abre una nueva).
        </div>
      )}

      {/* Viviendas que quedaron pendientes de revisita en jornadas
          anteriores de esta comunidad — la revisita queda en esta jornada */}
      {!sinRegistro && pendientesPrevias.length > 0 && (
        <div className="mb-3 rounded" style={{ border: "1px solid #f0d9a8", background: "#fffaf0", textAlign: "left" }}>
          <button onClick={() => setVerPendientesPrevias(v => !v)}
            style={{ width: "100%", background: "none", border: "none", padding: "10px 14px", textAlign: "left", fontSize: 13.5, fontWeight: 700, color: "#8a5a00" }}>
            {pendientesPrevias.length} vivienda(s) de jornadas anteriores pendientes de revisita {verPendientesPrevias ? "▲" : "▼"}
          </button>
          {verPendientesPrevias && (
            <div style={{ padding: "0 14px 10px", maxHeight: 320, overflowY: "auto" }}>
              {pendientesPrevias.map(r => (
                <div key={r.codigo_vivienda} className="d-flex justify-content-between align-items-center gap-2 py-2" style={{ borderTop: "1px solid #f3e6c8", fontSize: 13 }}>
                  <div>
                    <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{r.codigo_vivienda}</span>
                    {r.direccion ? ` · ${r.direccion}` : ""}
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      Último intento #{r.numero_visita || 1}: {r.estado_vivienda}{r.motivo_no_efectiva ? ` (${r.motivo_no_efectiva})` : ""} · {r.createdAt?.split("T")[0]}
                      {r.fecha_revisita_sugerida ? ` · revisita sugerida ${r.fecha_revisita_sugerida}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline-warning" onClick={() => abrirRevisita(r)}>Revisitar</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Barra de acciones y filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        {!sinRegistro && (
          <Button onClick={abrirNuevaVisita} style={{ background: "#B4462B", border: "none", fontWeight: 700 }}>
            Nueva visita
          </Button>
        )}

        <InputGroup style={{ maxWidth: 260, flex: 1 }}>
          <InputGroup.Text style={{ background: "#fff", border: "1.5px solid #dee2e6", borderRight: "none" }}>🔍</InputGroup.Text>
          <Form.Control
            placeholder="Código, dirección o entrevistado..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ fontSize: 13, border: "1.5px solid #dee2e6", borderLeft: "none" }}
          />
        </InputGroup>

        <SelectorJornada jornadas={jornadas} value={filtroJornada} onChange={onFiltroJornada} />

        <Form.Select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ maxWidth: 190, fontSize: 13 }}>
          <option value="">Todos los estados</option>
          <option value="Efectiva">Efectiva</option>
          <option value="No efectiva">No efectiva</option>
          <option value="Cerrada">Cerrada</option>
          <option value="Renuente">Renuente</option>
          <option value="No habitada">No habitada</option>
        </Form.Select>

        <Form.Select value={filtroEntrevistador} onChange={e => setFiltroEntrevistador(e.target.value)} style={{ maxWidth: 210, fontSize: 13 }}>
          <option value="">Todos los entrevistadores</option>
          {entrevistadoresPresentes.map(nom => <option key={nom} value={nom}>{nom}</option>)}
        </Form.Select>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 11.5, color: "#6c757d", whiteSpace: "nowrap" }}>Entre</label>
          <Form.Control type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} style={{ fontSize: 12.5, maxWidth: 145 }} />
          <span style={{ fontSize: 11.5, color: "#6c757d" }}>y</span>
          <Form.Control type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} style={{ fontSize: 12.5, maxWidth: 145 }} />
          {(fechaInicio || fechaFin) && (
            <button onClick={() => { setFechaInicio(""); setFechaFin(""); }} title="Quitar rango"
              style={{ background: "none", border: "none", color: "#b3261e", cursor: "pointer", fontSize: 14 }}>✕</button>
          )}
        </div>

        {puedeExportar && (
          <Button variant="outline-secondary" onClick={exportar} disabled={exportando} style={{ fontSize: 13 }}
            title={filtrados.length !== lista.length ? "Descarga solo las filas que deja el filtro de la tabla" : "Descarga toda la jornada"}>
            {exportando ? "Exportando..." : filtrados.length !== lista.length ? `Exportar Excel (${filtrados.length} filtrados)` : "Exportar Excel de la jornada"}
          </Button>
        )}
      </div>

      {/* Tabla */}
      {cargando ? (
        <p className="text-muted">Cargando...</p>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e9ecef" }}>
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ marginBottom: 0 }}>
              <thead style={{ background: "#f8faf8" }}>
                <tr>
                  <th style={TH}>Código vivienda</th>
                  <th style={TH}>Dirección</th>
                  <th style={TH}>Entrevistador</th>
                  <th style={TH}>Estado</th>
                  <th style={TH}>Fecha</th>
                  <th style={TH}>Sync</th>
                  <th style={TH}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>
                      <button onClick={() => verHistorial(r.codigo_vivienda)}
                        style={{ background: "none", border: "none", color: "#16342a", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", padding: 0 }}>
                        {r.codigo_vivienda}
                      </button>
                      {r.numero_visita > 1 && (
                        <div style={{ fontSize: 10, color: "#8a94a6" }}>Intento #{r.numero_visita}</div>
                      )}
                    </td>
                    <td style={{ ...TD, maxWidth: 160, whiteSpace: "normal" }}>{r.direccion || <span className="text-muted">—</span>}</td>
                    <td style={TD}>{r.entrevistador || <span className="text-muted">—</span>}</td>
                    <td style={TD}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 12.5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: ESTADO_DOT[r.estado_vivienda] }} />
                        {r.estado_vivienda}
                      </span>
                      {r.estado_vivienda === "No efectiva" && r.motivo_no_efectiva && (
                        <div style={{ fontSize: 10.5, color: "#6c757d" }}>
                          Motivo: {r.motivo_no_efectiva}
                        </div>
                      )}
                      {r.fecha_revisita_sugerida && (
                        <div style={{ fontSize: 10, color: "#b7791f", fontWeight: 700 }}>
                          Revisita: {r.fecha_revisita_sugerida}
                        </div>
                      )}
                    </td>
                    <td style={TD}>{r.createdAt?.split("T")[0]}</td>
                    <td style={TD}>
                      {r._pendiente
                        ? <span style={{ color: "#b7791f", fontSize: 11.5, fontWeight: 700 }}>⏳ pendiente</span>
                        : <span style={{ color: "#2d6a4f", fontSize: 11.5, fontWeight: 700 }}>✓ sincronizado</span>}
                    </td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => verDetalle(r)} title="Ver detalle"
                          style={{ ...BTN_ACCION, color: "#16342a" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#eef1e7"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}>
                          <IconoOjo />
                        </button>
                        {r.lat && r.lng && onVerEnMapa && (
                          <button onClick={() => onVerEnMapa(r.codigo_vivienda)} title="Ver en mapa"
                            style={{ ...BTN_ACCION, color: "#2d6a4f" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#e4f2e8"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoMapa />
                          </button>
                        )}
                        {r.foto_url && (
                          <button onClick={() => window.open(`${API.defaults.baseURL}${r.foto_url}`, "_blank")} title="Ver foto"
                            style={{ ...BTN_ACCION, color: "#0C447C" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#e7eef7"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoCamara />
                          </button>
                        )}
                        {!sinRegistro && requiereRevisita(r) && (
                          <button onClick={() => abrirRevisita(r)} title="Revisitar"
                            style={{ ...BTN_ACCION, color: "#b7791f" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#faf0dc"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoRevisitar />
                          </button>
                        )}
                        {puedeEditar(r) && !r._pendiente && (
                          <button onClick={() => abrirEditar(r)} title="Editar"
                            style={{ ...BTN_ACCION, color: "#0C447C" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#e7eef7"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoEditar />
                          </button>
                        )}
                        {puedeEditar(r) && r._pendiente && (
                          <button onClick={() => abrirEditar(r)} title="Editar borrador (todavía sin sincronizar) — funciona sin internet"
                            style={{ ...BTN_ACCION, color: "#b7791f" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fdf3e0"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoEditar />
                          </button>
                        )}
                        {esAdmin && !r._pendiente && (
                          <button onClick={() => eliminar(r)} title="Eliminar"
                            style={{ ...BTN_ACCION, color: "#b3261e" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fbe4e2"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            <IconoEliminar />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!paginados.length && (
                  <tr><td colSpan={7} style={{ ...TD, textAlign: "center", color: "#8a94a6", fontStyle: "italic" }}>Sin registros</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div style={{ padding: "10px 20px", background: "#f8faf8", borderTop: "1px solid #e9ecef", fontSize: 12, color: "#6c757d", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span>
              Mostrando <strong>{paginados.length}</strong> de <strong>{filtrados.length}</strong> · Total viviendas: <strong>{lista.length}</strong>
            </span>
            {totalPags > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>◀</button>
                <span style={{ fontWeight: 700 }}>{pagina}/{totalPags}</span>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
                  style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      {!soloLectura && (
        <BacSarampionModal
          show={showModal}
          onClose={() => { setShowModal(false); setViviendaBase(null); setModoRevisita(false); setVisitaEditar(null); }}
          reload={cargar}
          jornada={jornadaActiva}
          modoRevisita={modoRevisita}
          viviendaBase={viviendaBase}
          visitaEditar={visitaEditar}
        />
      )}


      <HistorialViviendaModal
        show={showHistorial}
        onClose={() => setShowHistorial(false)}
        codigoVivienda={codigoHistorial}
      />

      <VerVisitaModal
        show={showVer}
        onClose={() => setShowVer(false)}
        visita={visitaVer}
      />
    </div>
  );
}